// Contact Segments — saved lists that resolve to a live contact roster.
// Reps launch the Power Dialer straight from a segment. Marketing sends
// blasts to a segment. Segments are re-evaluated on every open, so a "Cold
// leads > 3 days" segment always reflects reality.
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, EmptyState } from '../ui.jsx';

const STAGES = ['New Lead','Contacted','Qualified','Estimate Sent','Booked','In Progress','Invoiced','Paid','Lost'];
const SOURCES = ['Missed Call','Inbound Call','Website Form','Booking Widget','Google Ads','Google Local Service Ad','Meta / Facebook Ad','TikTok Ad','Yelp','Angi','Thumbtack','Nextdoor','Door Hanger','Direct Mail','Referral — Existing Customer','Referral — Other Trade','Walk-in / Sign','Repeat Customer','Other'];

const EMPTY_FILTER = {
  stages: [],
  sources: [],
  hasPhone: true,
  hasEmail: false,
  excludeDnd: true,
  excludeOptedOut: true,
  wfNotRemoved: false,
  aiActive: false,
  lastContactOlderThan: '',
  createdWithin: '',
  searchText: '',
};

export function Segments({ toast }) {
  const [segments, setSegments] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, name, filter, ... }
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = () => api('/api/segments').then(d => setSegments(d.segments || [])).catch(e=>toast(e.message,'error'));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ name:'', description:'', filter: { ...EMPTY_FILTER }, is_favorite: false });
  const openEdit = s => setEditing({ id: s.id, name: s.name, description: s.description || '', filter: { ...EMPTY_FILTER, ...(s.filter||{}) }, is_favorite: !!s.is_favorite });

  const save = async () => {
    if (!editing?.name?.trim()) return toast('Name required', 'error');
    try {
      if (editing.id) {
        await api(`/api/segments/${editing.id}`, { method:'PATCH', body: editing });
        toast('✓ Segment updated');
      } else {
        await api('/api/segments', { method:'POST', body: editing });
        toast('✓ Segment created');
      }
      setEditing(null); load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const del = async (s) => {
    if (!confirm(`Delete segment "${s.name}"? (Contacts remain — just the saved filter is removed.)`)) return;
    try { await api(`/api/segments/${s.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch (e) { toast(e.message, 'error'); }
  };

  const runPreview = async () => {
    if (!editing) return;
    setPreviewing(true);
    try {
      const r = await api('/api/segments/preview', { method:'POST', body: { filter: editing.filter, limit: 200 }});
      setPreview(r.contacts || []);
    } catch (e) { toast(e.message, 'error'); setPreview([]); }
    finally { setPreviewing(false); }
  };

  const startDialing = async (s) => {
    try {
      const r = await api('/api/power-dialer/sessions', { method:'POST', body:{ segmentId: s.id, name: s.name }});
      // Store session id and hop to Power Dialer screen
      window.sessionStorage.setItem('activeDialerSessionId', r.id);
      window.location.hash = '#power-dialer';
      // Force re-render of tabs — App.jsx watches hash
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      toast(`📞 Dialer queued: ${r.queueLength} contacts`);
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!segments) return <Spinner sz={32}/>;

  return <div style={{ padding:'20px 24px', maxWidth:1200 }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14, gap:12 }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>📇 Segments</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Saved contact lists that re-resolve live. Launch the Power Dialer or a blast from any segment.</div>
      </div>
      <Btn v="primary" onClick={openNew}>+ New Segment</Btn>
    </div>

    {segments.length === 0 && <EmptyState icon="📇" title="No segments yet" body="Create your first — for example: New Leads not contacted in 24h + has phone + not on DNC — then launch the Power Dialer from it." ctaLabel="+ New Segment" onCta={openNew}/>}

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:14 }}>
      {segments.map(s => (
        <div key={s.id} style={{ ...styles.card, padding:16 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:6 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <strong style={{ fontSize:15 }}>{s.name} {s.is_favorite ? '⭐' : ''}</strong>
              {s.description && <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{s.description}</div>}
            </div>
          </div>
          <FilterSummary filter={s.filter}/>
          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            <Btn sz="sm" v="primary" onClick={()=>startDialing(s)}>📞 Dial</Btn>
            <Btn sz="sm" onClick={()=>openEdit(s)}>✏️ Edit</Btn>
            <Btn sz="sm" v="danger" onClick={()=>del(s)}>🗑</Btn>
          </div>
        </div>
      ))}
    </div>

    {editing && <SegmentEditor
      editing={editing} setEditing={setEditing}
      onSave={save} onCancel={()=>{ setEditing(null); setPreview(null); }}
      onPreview={runPreview} preview={preview} previewing={previewing}/>}
  </div>;
}

function FilterSummary({ filter }) {
  const f = filter || {};
  const parts = [];
  if (f.stages?.length) parts.push(`stages: ${f.stages.join(', ')}`);
  if (f.sources?.length) parts.push(`sources: ${f.sources.length} selected`);
  if (f.hasPhone) parts.push('has phone');
  if (f.hasEmail) parts.push('has email');
  if (f.excludeDnd) parts.push('no DND');
  if (f.excludeOptedOut) parts.push('no opt-outs');
  if (f.aiActive) parts.push('AI active');
  if (f.lastContactOlderThan) parts.push(`cold ${f.lastContactOlderThan}`);
  if (f.createdWithin) parts.push(`created within ${f.createdWithin}`);
  if (f.searchText) parts.push(`search: "${f.searchText}"`);
  return <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.5 }}>
    {parts.length ? parts.join(' · ') : <em>All contacts</em>}
  </div>;
}

function SegmentEditor({ editing, setEditing, onSave, onCancel, onPreview, preview, previewing }) {
  const setF = (patch) => setEditing({ ...editing, filter: { ...editing.filter, ...patch }});
  const toggleStage = (st) => {
    const cur = editing.filter.stages || [];
    setF({ stages: cur.includes(st) ? cur.filter(s=>s!==st) : [...cur, st] });
  };
  const toggleSource = (src) => {
    const cur = editing.filter.sources || [];
    setF({ sources: cur.includes(src) ? cur.filter(s=>s!==src) : [...cur, src] });
  };
  return <Modal open={true} onClose={onCancel} width={840}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>{editing.id ? 'Edit Segment' : 'New Segment'}</h2>
      <button onClick={onCancel} style={{ background:'none', border:'none', color:G.textMuted, fontSize:20, cursor:'pointer' }}>×</button>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
      <Field label="Name"><Input value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} placeholder="Cold new leads — 24h+"/></Field>
      <Field label="Description (optional)"><Input value={editing.description} onChange={e=>setEditing({...editing, description:e.target.value})} placeholder="Reps: rip through these first thing every morning"/></Field>
    </div>

    <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Stages</div>
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
      {STAGES.map(st => {
        const on = (editing.filter.stages || []).includes(st);
        return <button key={st} onClick={()=>toggleStage(st)}
          style={{ padding:'6px 12px', borderRadius:16, border:`1px solid ${on?G.purple:G.border}`, background: on?G.purpleDim:G.card, color: on?G.purple:G.text, fontSize:12, cursor:'pointer' }}>{st}</button>;
      })}
    </div>

    <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Sources</div>
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12, maxHeight:120, overflowY:'auto' }}>
      {SOURCES.map(s => {
        const on = (editing.filter.sources || []).includes(s);
        return <button key={s} onClick={()=>toggleSource(s)}
          style={{ padding:'4px 10px', borderRadius:14, border:`1px solid ${on?G.cyan:G.border}`, background: on?G.cyan+'22':G.card, color: on?G.cyan:G.text, fontSize:11, cursor:'pointer' }}>{s}</button>;
      })}
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
      <Field label="Cold — last contact older than">
        <Input value={editing.filter.lastContactOlderThan} onChange={e=>setF({ lastContactOlderThan:e.target.value })} placeholder="e.g. 3 days"/>
      </Field>
      <Field label="Created within">
        <Input value={editing.filter.createdWithin} onChange={e=>setF({ createdWithin:e.target.value })} placeholder="e.g. 7 days"/>
      </Field>
      <Field label="Text search (name/phone/email/tags)">
        <Input value={editing.filter.searchText} onChange={e=>setF({ searchText:e.target.value })}/>
      </Field>
    </div>

    <div style={{ display:'flex', flexWrap:'wrap', gap:14, marginBottom:14 }}>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.filter.hasPhone} onChange={e=>setF({ hasPhone:e.target.checked })}/> Has phone
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.filter.hasEmail} onChange={e=>setF({ hasEmail:e.target.checked })}/> Has email
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.filter.excludeDnd} onChange={e=>setF({ excludeDnd:e.target.checked })}/> Exclude DND
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.filter.excludeOptedOut} onChange={e=>setF({ excludeOptedOut:e.target.checked })}/> Exclude opt-outs
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.filter.aiActive} onChange={e=>setF({ aiActive:e.target.checked })}/> AI active only
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editing.is_favorite} onChange={e=>setEditing({ ...editing, is_favorite:e.target.checked })}/> ⭐ Favorite
      </label>
    </div>

    <div style={{ borderTop:`1px solid ${G.border}`, paddingTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <Btn onClick={onPreview}>{previewing ? 'Loading…' : '🔍 Preview matches'}</Btn>
      <div style={{ display:'flex', gap:6 }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn v="primary" onClick={onSave}>Save Segment</Btn>
      </div>
    </div>

    {preview != null && <div style={{ marginTop:14, padding:'10px 14px', background:G.input, borderRadius:8 }}>
      <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>{preview.length} matching contact{preview.length===1?'':'s'} (max 200 shown)</div>
      <div style={{ maxHeight:200, overflowY:'auto', fontSize:12 }}>
        {preview.slice(0, 50).map(c => (
          <div key={c.id} style={{ padding:'4px 0', borderBottom:`1px solid ${G.border}` }}>
            <strong>{c.first_name} {c.last_name}</strong> · <span style={{ color:G.textMuted }}>{c.stage} · {c.phone || c.email || '—'}</span>
          </div>
        ))}
        {preview.length > 50 && <div style={{ padding:'8px 0', color:G.textMuted, textAlign:'center' }}>… +{preview.length - 50} more</div>}
      </div>
    </div>}
  </Modal>;
}
